import { useCallback, useRef, useEffect } from "react";
import { notification } from "antd";
import axiosInstance from "../core/index-core";
import { useGenerationProgressNotifications } from "./useGenerationProgressNotifications";

// Phased polling intervals
const STEP_DOWN_INTERVALS = [20000, 15000, 10000, 8000];
const ACTIVE_FLOOR = 5000;
const RELAXED_INTERVAL = 15000;
const RELAXED_PHASE_THRESHOLD = 5 * 60 * 1000;
const WARNING_REPEAT_INTERVAL = 5 * 60 * 1000;
// After generation reports "completed", the GET /ai-note endpoint can briefly
// still return the in-progress marker (the status flips before the persisted
// note is served). Keep re-fetching the finished note this many times before
// giving up, so a completed generation always delivers its note instead of
// terminating the poll and stranding the caller on its loading state.
const MAX_COMPLETED_FETCH_ATTEMPTS = 15;

/**
 * Unified AI note polling hook.
 *
 * Handles two flows:
 *   1. Generation-id polling — polls GET /ai-note/generation/{id} (preferred)
 *   2. Note fetch — calls GET /ai-note, detects in-progress generation
 *
 * Phased intervals: 20s \u2192 15s \u2192 10s \u2192 8s \u2192 5s floor, 15s relaxed after 5 min.
 *
 * Callbacks let each consumer wire its own side-effects.
 */
export const useAiNotePolling = ({ patientId, sessionId, onNoteReady, onFailed, onGenerating }) => {
  const pollTimeoutRef = useRef(null);
  const abortRef = useRef(false);

  // Use refs for callbacks to avoid stale closures in the polling loop
  const onNoteReadyRef = useRef(onNoteReady);
  const onFailedRef = useRef(onFailed);
  const onGeneratingRef = useRef(onGenerating);
  useEffect(() => { onNoteReadyRef.current = onNoteReady; }, [onNoteReady]);
  useEffect(() => { onFailedRef.current = onFailed; }, [onFailed]);
  useEffect(() => { onGeneratingRef.current = onGenerating; }, [onGenerating]);

  const {
    startGenerationProgressNotifications,
    stopGenerationProgressNotifications,
  } = useGenerationProgressNotifications();

  const clearPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Fetch GET /ai-note — returns the note or detects in-progress generation
  const fetchNote = useCallback(async () => {
    if (!patientId || !sessionId) return { done: true, status: 0 };
    try {
      const res = await axiosInstance.get(
        `patient/${patientId}/sessions/${sessionId}/ai-note`,
        { validateStatus: (s) => s !== 401 }
      );

      if (res.status === 200 && res.data) {
        // Detect generation-in-progress (backend returns HTTP 200, not 202)
        if (res.data.status === "generating" && res.data.generation_id) {
          return { done: false, status: 202, generationId: res.data.generation_id };
        }
        // Detect recently failed generation surfaced by backend on reload
        if (res.data.status === "failed") {
          return { done: true, status: 200, failed: true, error: res.data.error, error_code: res.data.error_code };
        }
        return { done: true, status: 200, noteData: res.data };
      }
      if (res.status === 204) return { done: false, status: 204 };
      if (res.status === 404) return { done: false, status: 404 };

      if (res.data?.error_code === "UNSUPPORTED_SESSION_TYPE") {
        window.dispatchEvent(new CustomEvent("session:unsupported-type"));
        return { done: true, status: res.status, unsupportedType: true };
      }

      return { done: false, status: res.status };
    } catch (e) {
      if (e?.response?.data?.error_code === "UNSUPPORTED_SESSION_TYPE") {
        window.dispatchEvent(new CustomEvent("session:unsupported-type"));
        return { done: true, status: e.response.status, unsupportedType: true };
      }
      return { done: false, status: 0 };
    }
  }, [patientId, sessionId]);

  // Poll GET /ai-note/generation/{id}
  const fetchGenerationStatus = useCallback(async (generationId) => {
    if (!generationId || !patientId || !sessionId) return null;
    try {
      const res = await axiosInstance.get(
        `patient/${patientId}/sessions/${sessionId}/ai-note/generation/${generationId}`,
        { validateStatus: (s) => s !== 401 }
      );
      return res.status === 200 ? res.data : null;
    } catch {
      return null;
    }
  }, [patientId, sessionId]);

  // Main polling loop
  const startPolling = useCallback(({ generationId = null } = {}) => {
    clearPolling();
    abortRef.current = false;
    let attempts = 0;
    let completedFetchAttempts = 0;
    const startTime = Date.now();
    let lastWarningTime = 0;

    const getNextDelay = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= RELAXED_PHASE_THRESHOLD) return RELAXED_INTERVAL;
      if (attempts <= STEP_DOWN_INTERVALS.length) return STEP_DOWN_INTERVALS[attempts - 1];
      return ACTIVE_FLOOR;
    };

    const maybeShowWarning = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= RELAXED_PHASE_THRESHOLD && elapsed - lastWarningTime >= WARNING_REPEAT_INTERVAL) {
        lastWarningTime = elapsed;
        notification.warning({
          key: "ai-note-generation-slow",
          message: "AI note generation is taking longer than usual",
          description: "Generation is still in progress. Please wait \u2014 your AI note will appear automatically once ready.",
          duration: 30,
        });
      }
    };

    const cleanup = () => {
      stopGenerationProgressNotifications();
      notification.destroy("ai-note-generation-slow");
    };

    const scheduleNext = () => {
      maybeShowWarning();
      pollTimeoutRef.current = setTimeout(step, getNextDelay());
    };

    const step = async () => {
      if (abortRef.current) return;
      attempts += 1;

      // If we have a generation_id, poll the status endpoint
      if (generationId) {
        const statusData = await fetchGenerationStatus(generationId);
        const genStatus = statusData?.status;

        if (genStatus === "completed") {
          const result = await fetchNote();
          // If we were aborted during the await (unmount / session change /
          // stopPolling), do NOT deliver — a late delivery from an old session
          // could write the previous patient's note into the new session.
          if (abortRef.current) return;
          // The backend surfaced a REAL failure on the note endpoint (e.g. a
          // failed re-generation / transcript-not-found on a churny session).
          // Report that failure as-is — do NOT retry it into the misleading
          // "finished generating but could not be loaded" timeout message.
          if (result.failed) {
            cleanup();
            onFailedRef.current?.({ error: result.error, error_code: result.error_code });
            return;
          }
          if (result.status === 200 && result.noteData) {
            cleanup();
            onNoteReadyRef.current?.(result.noteData);
            return;
          }
          // Generation finished, but the note isn't served yet (the status
          // endpoint can flip to "completed" before GET /ai-note returns the
          // persisted note). Keep polling so the finished note is delivered
          // once available. Terminating here would leave the caller stuck on
          // its loading state forever (the 2026-07-13 blank-note incident).
          completedFetchAttempts += 1;
          if (completedFetchAttempts >= MAX_COMPLETED_FETCH_ATTEMPTS) {
            cleanup();
            onFailedRef.current?.({
              error:
                "The AI note finished generating but could not be loaded. Please refresh the page.",
              error_code: "NOTE_FETCH_TIMEOUT",
            });
            return;
          }
          scheduleNext();
          return;
        }

        if (genStatus === "failed") {
          cleanup();
          onFailedRef.current?.(statusData);
          return;
        }

        // Still pending/processing
        scheduleNext();
        return;
      }

      // No generation_id — fetch the note directly to discover one
      const result = await fetchNote();

      if (result.generationId) {
        // Found an in-progress generation — switch to generation-id polling
        onGeneratingRef.current?.();
        generationId = result.generationId;
        scheduleNext();
        return;
      }

      if (result.done && result.status === 200 && result.noteData) {
        cleanup();
        onNoteReadyRef.current?.(result.noteData);
        return;
      }

      if (abortRef.current) return;

      // Still waiting (204, 404, error) — keep polling
      scheduleNext();
    };

    startGenerationProgressNotifications();
    step();
  }, [fetchNote, fetchGenerationStatus, clearPolling, startGenerationProgressNotifications, stopGenerationProgressNotifications]);

  const stopPolling = useCallback(() => {
    abortRef.current = true;
    clearPolling();
    stopGenerationProgressNotifications();
    notification.destroy("ai-note-generation-slow");
  }, [clearPolling, stopGenerationProgressNotifications]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current = true;
      clearPolling();
    };
  }, [clearPolling]);

  return { startPolling, stopPolling, fetchNote };
};
